// Result screen (design/booth-v2 BoothResult): big strip, honest save status
// driven by the real upload/finalize result, post / download / shoot again.

import { h } from './dom';
import { icon } from './booth-icons';
import type { GalleryItem } from '../booth/gallery';

export type SaveState =
  | { kind: 'quick' }
  | { kind: 'saving' }
  /** v0.9: stored; the AI is still cutting out and composing the final photos */
  | { kind: 'finishing'; since: number }
  | { kind: 'saved' }
  | { kind: 'error'; msg: string; retry?: () => Promise<void> };

export interface ResultHooks {
  isHost: boolean;
  /** server-stored room: posting to the feed is possible once saved */
  savedRoom: boolean;
  items(): GalleryItem[];
  state(sessionId: string): SaveState;
  post(sessionId: string): Promise<void>;
  open(item: GalleryItem): Promise<void>;
  again(): void;
}

export function createResult(hooks: ResultHooks) {
  let current: string | null = null;
  const img = h('img', { alt: 'Dải ảnh vừa chụp' });
  const stripBox = h('div.bth-rstrip', {}, img);
  const status = h('p.bth-rstatus', { role: 'status', 'aria-live': 'polite' });
  const thumbs = h('div.bth-rthumbs', { 'aria-label': 'Ảnh trong phòng' });
  const postBtn = h('button.main', {}, 'Đăng lên bảng tin');
  const saveBtn = h('button', {}, 'Tải về máy');
  const againBtn = h('button', {}, hooks.isHost ? 'Chụp lại' : 'Quay lại phòng');
  const why = h('p.why');
  const el = h('section.bth-result', { hidden: true, 'aria-label': 'Kết quả' },
    h('header.bth-rhead', {},
      h('button', { 'aria-label': 'Đóng', onclick: () => hide() }, icon('close', 22, 2.2)),
      h('h1', {}, 'Xong rồi!'),
      h('span')),
    stripBox, status, thumbs,
    h('div.bth-ractions', {}, postBtn, h('div.row2', {}, saveBtn, againBtn), why));

  let posting = false, postErr = '';
  postBtn.onclick = async () => {
    if (!current || posting) return;
    posting = true; postErr = '';
    render();
    try {
      await hooks.post(current);
    } catch (e) {
      postErr = 'Không mở được bài đăng: ' + (e as Error).message;
    } finally {
      posting = false;
      render();
    }
  };
  let saving = false;
  saveBtn.onclick = async () => {
    const it = mainItem();
    if (!it || saving) return;
    saving = true; render();
    try { await hooks.open(it); } finally { saving = false; render(); }
  };
  againBtn.onclick = () => { hide(); hooks.again(); };

  const sessionItems = () => hooks.items().filter((i) => i.sessionId === current);
  const mainItem = () => {
    const its = sessionItems();
    return its.find((i) => i.kind === 'strip') ?? its[its.length - 1] ?? null;
  };

  function render() {
    if (!current) return;
    const main = mainItem();
    const st0 = hooks.state(current);
    if (main?.previewUrl) {
      // v0.9.1: the phone's own AI picture right away; the server's final one replaces it
      if (img.src !== main.previewUrl) img.src = main.previewUrl;
      stripBox.replaceChildren(img, ...(st0.kind === 'finishing'
        ? [h('div.bth-improving', {}, h('span.bth-spin'), 'Đang hoàn thiện bằng AI máy chủ…')] : []));
    } else stripBox.replaceChildren(h('div.spinner'));

    const st = hooks.state(current);
    status.className = 'bth-rstatus';
    if (st.kind === 'saved') {
      status.classList.add('ok');
      status.replaceChildren(icon('check', 16, 2.4), 'Đã lưu vào Thư viện của cả hai');
    } else if (st.kind === 'finishing') {
      status.replaceChildren(h('span.bth-spin'), `Ảnh xem trước từ AI trên máy · bản hoàn thiện sau ${Math.max(0, Math.round((Date.now() - st.since) / 1000))} s…`);
    } else if (st.kind === 'saving') {
      status.replaceChildren(h('span.bth-spin'), 'Đang lưu vào Thư viện…');
    } else if (st.kind === 'quick') {
      status.replaceChildren('Chế độ nhanh: ảnh chỉ có bản xem trước, chưa lưu vào Thư viện');
    } else {
      status.classList.add('err');
      const kids: (Node | string)[] = [icon('alert', 16, 2), st.msg];
      if (st.retry) {
        const retry = st.retry;
        const b = h('button.mini', {}, icon('retry', 14, 2.2), 'Thử lại');
        b.onclick = async () => {
          b.disabled = true;
          b.replaceChildren(h('span.bth-spin'), 'Đang thử lại…');
          try { await retry(); } catch { /* state() now carries the new error */ }
          render();
        };
        kids.push(b);
      }
      status.replaceChildren(...kids);
    }

    const all = hooks.items();
    thumbs.replaceChildren(...all.map((it) => {
      const b = h('button.bth-rthumb', {
        class: it === main ? 'on' : '',
        'aria-label': it.kind === 'strip' ? 'Dải ảnh' : `Kiểu ${it.index + 1}`,
        onclick: () => {
          if (it.kind === 'strip' && it.sessionId !== current) { current = it.sessionId; render(); } else void hooks.open(it);
        },
      }, it.previewUrl ? h('img', { src: it.previewUrl, alt: '' }) : h('span.bth-spin'),
      h('small', {}, it.kind === 'strip' ? 'Dải' : `#${it.index + 1}`));
      return b;
    }));

    const canPost = hooks.savedRoom && st.kind === 'saved';
    postBtn.disabled = !canPost || posting;
    postBtn.replaceChildren(...(posting ? [h('span.bth-spin'), 'Đang mở…'] : ['Đăng lên bảng tin']));
    saveBtn.disabled = !main || saving;
    saveBtn.replaceChildren(...(saving ? [h('span.bth-spin'), 'Đang mở…'] : ['Tải về máy']));
    why.textContent = postErr || (!hooks.savedRoom
      ? 'Đăng nhập và chụp trong một phòng đã lưu để đăng lên bảng tin.'
      : st.kind === 'saving' ? 'Đăng được ngay khi ảnh đã lưu xong.'
        : st.kind === 'error' ? 'Ảnh chưa lưu nên chưa đăng lên bảng tin được – bạn vẫn tải bản xem trước về máy.' : '');
  }

  function show(sessionId: string) {
    current = sessionId;
    el.hidden = false;
    render();
  }
  function hide() {
    el.hidden = true;
  }

  return { el, show, hide, render, get open() { return !el.hidden; }, get session() { return current; } };
}
